/**
 * KATA Board Store
 * Manages KATA Board state using Zustand
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { 
  KATACycle, 
  PDCAStep, 
  Experiment, 
  KATANote, 
  WorkshopKATA,
  KATABoardData,
  KATADashboardStats,
  kataCycleApi,
  pdcaStepApi,
  experimentApi,
  kataNoteApi,
  workshopKataApi
} from '../services/kataBoard';

interface KATABoardState {
  // Data
  cycles: KATACycle[];
  currentCycle: KATACycle | null;
  currentWorkshop: WorkshopKATA | null;
  boardData: KATABoardData | null;
  dashboardStats: KATADashboardStats | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  selectedStep: PDCAStep | null;
  selectedExperiment: Experiment | null;
  showCreateCycleModal: boolean;
  showCreateExperimentModal: boolean;
  showCreateNoteModal: boolean;
  
  // Actions
  fetchCycles: (workshopId?: number) => Promise<void>;
  fetchCycle: (id: number) => Promise<void>;
  createCycle: (data: any) => Promise<void>;
  updateCycle: (id: number, data: any) => Promise<void>;
  deleteCycle: (id: number) => Promise<void>;
  advanceStep: (cycleId: number) => Promise<void>;
  startNewCycle: (cycleId: number, data: any) => Promise<void>;
  
  fetchWorkshopBoard: (workshopId: number) => Promise<void>;
  fetchDashboardStats: () => Promise<void>;
  
  updateStep: (id: number, data: any) => Promise<void>;
  markStepCurrent: (id: number) => Promise<void>;
  
  createExperiment: (data: any) => Promise<void>;
  updateExperiment: (id: number, data: any) => Promise<void>;
  completeExperiment: (id: number, data: any) => Promise<void>;
  deleteExperiment: (id: number) => Promise<void>;
  
  createNote: (data: any) => Promise<void>;
  updateNote: (id: number, data: any) => Promise<void>;
  deleteNote: (id: number) => Promise<void>;
  
  // UI Actions
  setSelectedStep: (step: PDCAStep | null) => void;
  setSelectedExperiment: (experiment: Experiment | null) => void;
  setShowCreateCycleModal: (show: boolean) => void;
  setShowCreateExperimentModal: (show: boolean) => void;
  setShowCreateNoteModal: (show: boolean) => void;
  clearError: () => void;
  reset: () => void;
}

export const useKATABoardStore = create<KATABoardState>()(
  devtools(
    (set, get) => ({
      // Initial state
      cycles: [],
      currentCycle: null,
      currentWorkshop: null,
      boardData: null,
      dashboardStats: null,
      
      loading: false,
      error: null,
      selectedStep: null,
      selectedExperiment: null,
      showCreateCycleModal: false,
      showCreateExperimentModal: false,
      showCreateNoteModal: false,

      // KATA Cycle Actions
      fetchCycles: async (workshopId?: number) => {
        set({ loading: true, error: null });
        try {
          const params = workshopId ? { workshop: workshopId } : undefined;
          const response = await kataCycleApi.list(params);
          set({ cycles: response.data, loading: false });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to fetch KATA cycles',
            loading: false 
          });
        }
      },

      fetchCycle: async (id: number) => {
        set({ loading: true, error: null });
        try {
          const response = await kataCycleApi.get(id);
          set({ currentCycle: response.data, loading: false });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to fetch KATA cycle',
            loading: false 
          });
        }
      },

      createCycle: async (data: any) => {
        set({ loading: true, error: null });
        try {
          const response = await kataCycleApi.create(data);
          const { cycles } = get();
          set({ 
            cycles: [response.data, ...cycles],
            currentCycle: response.data,
            loading: false,
            showCreateCycleModal: false
          });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to create KATA cycle',
            loading: false 
          });
        }
      },

      updateCycle: async (id: number, data: any) => {
        set({ loading: true, error: null });
        try {
          const response = await kataCycleApi.update(id, data);
          const { cycles, currentCycle } = get();
          
          const updatedCycles = cycles.map(cycle => 
            cycle.id === id ? response.data : cycle
          );
          
          set({ 
            cycles: updatedCycles,
            currentCycle: currentCycle?.id === id ? response.data : currentCycle,
            loading: false 
          });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to update KATA cycle',
            loading: false 
          });
        }
      },

      deleteCycle: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await kataCycleApi.delete(id);
          const { cycles, currentCycle } = get();
          
          const updatedCycles = cycles.filter(cycle => cycle.id !== id);
          
          set({ 
            cycles: updatedCycles,
            currentCycle: currentCycle?.id === id ? null : currentCycle,
            loading: false 
          });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to delete KATA cycle',
            loading: false 
          });
        }
      },

      advanceStep: async (cycleId: number) => {
        set({ loading: true, error: null });
        try {
          await kataCycleApi.advanceStep(cycleId);
          // Refresh the current cycle to get updated steps
          await get().fetchCycle(cycleId);
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to advance step',
            loading: false 
          });
        }
      },

      startNewCycle: async (cycleId: number, data: any) => {
        set({ loading: true, error: null });
        try {
          const response = await kataCycleApi.startNewCycle(cycleId, data);
          const { cycles } = get();
          set({ 
            cycles: [response.data, ...cycles],
            currentCycle: response.data,
            loading: false 
          });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to start new cycle',
            loading: false 
          });
        }
      },

      // Workshop Board Actions
      fetchWorkshopBoard: async (workshopId: number) => {
        set({ loading: true, error: null });
        try {
          const [workshopResponse, boardResponse] = await Promise.all([
            workshopKataApi.get(workshopId),
            workshopKataApi.getKataBoard(workshopId)
          ]);
          
          set({ 
            currentWorkshop: workshopResponse.data,
            boardData: boardResponse.data,
            cycles: boardResponse.data.kata_cycles,
            currentCycle: boardResponse.data.active_cycle || null,
            loading: false 
          });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to fetch workshop board',
            loading: false 
          });
        }
      },

      fetchDashboardStats: async () => {
        set({ loading: true, error: null });
        try {
          const response = await kataCycleApi.getDashboardStats();
          set({ dashboardStats: response.data, loading: false });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to fetch dashboard stats',
            loading: false 
          });
        }
      },

      // PDCA Step Actions
      updateStep: async (id: number, data: any) => {
        set({ loading: true, error: null });
        try {
          const response = await pdcaStepApi.update(id, data);
          const { currentCycle } = get();
          
          if (currentCycle) {
            const updatedSteps = currentCycle.pdca_steps.map(step => 
              step.id === id ? response.data : step
            );
            
            set({ 
              currentCycle: { ...currentCycle, pdca_steps: updatedSteps },
              loading: false 
            });
          }
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to update step',
            loading: false 
          });
        }
      },

      markStepCurrent: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await pdcaStepApi.markCurrent(id);
          const { currentCycle } = get();
          
          if (currentCycle) {
            // Refresh the cycle to get updated step states
            await get().fetchCycle(currentCycle.id);
          }
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to mark step as current',
            loading: false 
          });
        }
      },

      // Experiment Actions
      createExperiment: async (data: any) => {
        set({ loading: true, error: null });
        try {
          const response = await experimentApi.create(data);
          const { currentCycle } = get();
          
          if (currentCycle) {
            const updatedSteps = currentCycle.pdca_steps.map(step => {
              if (step.id === data.pdca_step) {
                return {
                  ...step,
                  experiments: [...step.experiments, response.data]
                };
              }
              return step;
            });
            
            set({ 
              currentCycle: { ...currentCycle, pdca_steps: updatedSteps },
              loading: false,
              showCreateExperimentModal: false
            });
          }
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to create experiment',
            loading: false 
          });
        }
      },

      updateExperiment: async (id: number, data: any) => {
        set({ loading: true, error: null });
        try {
          const response = await experimentApi.update(id, data);
          const { currentCycle } = get();
          
          if (currentCycle) {
            const updatedSteps = currentCycle.pdca_steps.map(step => ({
              ...step,
              experiments: step.experiments.map(exp => 
                exp.id === id ? response.data : exp
              )
            }));
            
            set({ 
              currentCycle: { ...currentCycle, pdca_steps: updatedSteps },
              loading: false 
            });
          }
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to update experiment',
            loading: false 
          });
        }
      },

      completeExperiment: async (id: number, data: any) => {
        set({ loading: true, error: null });
        try {
          await experimentApi.complete(id, data);
          const { currentCycle } = get();
          
          if (currentCycle) {
            // Refresh the cycle to get updated experiment data
            await get().fetchCycle(currentCycle.id);
          }
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to complete experiment',
            loading: false 
          });
        }
      },

      deleteExperiment: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await experimentApi.delete(id);
          const { currentCycle } = get();
          
          if (currentCycle) {
            const updatedSteps = currentCycle.pdca_steps.map(step => ({
              ...step,
              experiments: step.experiments.filter(exp => exp.id !== id)
            }));
            
            set({ 
              currentCycle: { ...currentCycle, pdca_steps: updatedSteps },
              loading: false 
            });
          }
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to delete experiment',
            loading: false 
          });
        }
      },

      // Note Actions
      createNote: async (data: any) => {
        set({ loading: true, error: null });
        try {
          const response = await kataNoteApi.create(data);
          const { currentCycle } = get();
          
          if (currentCycle) {
            if (data.pdca_step) {
              // Add to step notes
              const updatedSteps = currentCycle.pdca_steps.map(step => {
                if (step.id === data.pdca_step) {
                  return {
                    ...step,
                    notes: [...step.notes, response.data]
                  };
                }
                return step;
              });
              
              set({ 
                currentCycle: { ...currentCycle, pdca_steps: updatedSteps },
                loading: false,
                showCreateNoteModal: false
              });
            } else {
              // Add to cycle notes
              set({ 
                currentCycle: { 
                  ...currentCycle, 
                  notes: [...currentCycle.notes, response.data] 
                },
                loading: false,
                showCreateNoteModal: false
              });
            }
          }
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to create note',
            loading: false 
          });
        }
      },

      updateNote: async (id: number, data: any) => {
        set({ loading: true, error: null });
        try {
          const response = await kataNoteApi.update(id, data);
          const { currentCycle } = get();
          
          if (currentCycle) {
            // Update in cycle notes
            const updatedCycleNotes = currentCycle.notes.map(note => 
              note.id === id ? response.data : note
            );
            
            // Update in step notes
            const updatedSteps = currentCycle.pdca_steps.map(step => ({
              ...step,
              notes: step.notes.map(note => 
                note.id === id ? response.data : note
              )
            }));
            
            set({ 
              currentCycle: { 
                ...currentCycle, 
                notes: updatedCycleNotes,
                pdca_steps: updatedSteps
              },
              loading: false 
            });
          }
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to update note',
            loading: false 
          });
        }
      },

      deleteNote: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await kataNoteApi.delete(id);
          const { currentCycle } = get();
          
          if (currentCycle) {
            // Remove from cycle notes
            const updatedCycleNotes = currentCycle.notes.filter(note => note.id !== id);
            
            // Remove from step notes
            const updatedSteps = currentCycle.pdca_steps.map(step => ({
              ...step,
              notes: step.notes.filter(note => note.id !== id)
            }));
            
            set({ 
              currentCycle: { 
                ...currentCycle, 
                notes: updatedCycleNotes,
                pdca_steps: updatedSteps
              },
              loading: false 
            });
          }
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to delete note',
            loading: false 
          });
        }
      },

      // UI Actions
      setSelectedStep: (step: PDCAStep | null) => set({ selectedStep: step }),
      setSelectedExperiment: (experiment: Experiment | null) => set({ selectedExperiment: experiment }),
      setShowCreateCycleModal: (show: boolean) => set({ showCreateCycleModal: show }),
      setShowCreateExperimentModal: (show: boolean) => set({ showCreateExperimentModal: show }),
      setShowCreateNoteModal: (show: boolean) => set({ showCreateNoteModal: show }),
      clearError: () => set({ error: null }),
      reset: () => set({
        cycles: [],
        currentCycle: null,
        currentWorkshop: null,
        boardData: null,
        dashboardStats: null,
        loading: false,
        error: null,
        selectedStep: null,
        selectedExperiment: null,
        showCreateCycleModal: false,
        showCreateExperimentModal: false,
        showCreateNoteModal: false,
      }),
    }),
    {
      name: 'kata-board-store',
    }
  )
);
